import { useState } from "react";
import { toast } from "sonner";
import { Check } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { openExternal } from "@/lib/redirect";
import { useMe } from "@/components/app/TrialBanner";
import { UsageChart } from "@/components/billing/UsageChart";
import { PeriodToggle, PriceTag } from "@/components/billing/PeriodToggle";
import { CancelPlan } from "@/components/billing/CancelPlan";

const PLANS = [
  { id: "starter", name: "Starter", price: 29, perks: ["Multi-Sync dashboard", "Zero Cash Date tracking", "25 AI calibrations / day", "Expense categorization"] },
  { id: "pro", name: "Pro", price: 79, perks: ["Everything in Starter", "100 AI calibrations / day", "Vendor cut-off modeling", "Multi-bank accounts", "Priority support"] },
];

export default function Billing() {
  const api = useApi();
  const { data: me } = useMe();
  const [busy, setBusy] = useState(null);
  const currentKey = me?.price_key || (["starter", "pro"].includes(me?.plan) ? `${me.plan}_monthly` : null);
  const [period, setPeriod] = useState(null);
  const shown = period || (currentKey?.endsWith("_yearly") ? "yearly" : "monthly");

  const qc = useQueryClient();
  const [nudgeHidden, setNudgeHidden] = useState(false);
  const dismissNudge = async () => {
    setNudgeHidden(true);
    try { await api.post("/payments/yearly-nudge/dismiss"); qc.invalidateQueries({ queryKey: ["me"] }); } catch { /* hidden locally anyway */ }
  };
  const checkout = async (key) => {
    setBusy(key);
    try {
      if (["starter", "pro"].includes(me?.plan)) {
        const r = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
        if (r.switched) { toast.success(`Switched to ${r.plan === "pro" ? "Pro" : "Starter"} (${key.endsWith("_yearly") ? "yearly" : "monthly"})`); await qc.invalidateQueries(); }
        else await openExternal(async () => r.checkout_url);
      } else {
        await openExternal(async () => (await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin })).checkout_url);
      }
    } catch (e) { toast.error(errText(e, "Couldn't start checkout")); }
    setBusy(null);
  };

  return (
    <div data-testid="billing-page">
      <p className="font-mono text-xs text-[#A3A3A3]">BILLING</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">Your plan</h1>
      <p data-testid="current-plan" className="mt-3 text-[#A3A3A3]">
        Current: <span className="font-mono text-[#00FF9D] uppercase">{me?.plan || "…"}</span>
        {currentKey && me?.plan !== "trial" && <span data-testid="current-period"> · billed {currentKey.endsWith("_yearly") ? "yearly" : "monthly"}</span>}
        {me?.plan === "trial" && <> · trial ends {new Date(me.trial_ends_at).toLocaleDateString()}</>}
      </p>
      <div className="mt-6"><PeriodToggle value={shown} onChange={setPeriod} /></div>
      {me?.yearly_nudge && !nudgeHidden && (
        <div data-testid="yearly-nudge" className="mt-5 max-w-4xl flex flex-wrap items-center gap-3 rounded-xl border border-[#00FF9D]/40 bg-[#00FF9D]/[0.06] px-5 py-4">
          <p className="text-sm flex-1 min-w-[240px]">
            <span className="font-semibold text-white">Save ${me.yearly_savings?.toFixed(2)} a year.</span>
            <span className="text-[#A3A3A3]"> You've been on monthly billing for 3+ months. Yearly is 10% less, and your unused month is credited.</span>
          </p>
          {shown === "yearly" && me.plan in { starter: 1, pro: 1 } ? (
            <button data-testid="yearly-nudge-switch-btn" disabled={!!busy} onClick={() => checkout(`${me.plan}_yearly`)}
              className="rounded-full bg-[#00FF9D] px-4 py-2 text-xs font-semibold text-[#050505] hover:bg-white transition-colors disabled:opacity-50">{busy ? "Switching…" : "Switch to yearly"}</button>
          ) : (
            <button data-testid="yearly-nudge-switch-btn" onClick={() => setPeriod("yearly")}
              className="rounded-full bg-[#00FF9D] px-4 py-2 text-xs font-semibold text-[#050505] hover:bg-white transition-colors">See yearly price</button>
          )}
          <button data-testid="yearly-nudge-dismiss-btn" onClick={dismissNudge} aria-label="Dismiss"
            className="text-xs text-[#A3A3A3] hover:text-white transition-colors">Not now</button>
        </div>
      )}
      <div className="mt-6 grid md:grid-cols-2 gap-4 max-w-4xl">
        {PLANS.map((p) => {
          const key = `${p.id}_${shown}`;
          const current = me?.plan !== "trial" && currentKey === key;
          const samePlan = me?.plan === p.id && me?.plan !== "trial";
          return (
            <div key={p.id} data-testid={`billing-plan-${p.id}`} className={`rounded-xl border p-7 ${current ? "border-[#00FF9D]" : "border-[#2A2A2A]"} bg-[#121212]`}>
              <h3 className="font-display text-2xl">{p.name}</h3>
              <div className="mt-4"><PriceTag monthly={p.price} period={shown} testid={`billing-price-${p.id}`} /></div>
              <ul className="mt-6 space-y-2 text-sm">{p.perks.map((k) => <li key={k} className="flex gap-2"><Check className="text-[#00FF9D] mt-0.5" />{k}</li>)}</ul>
              <button data-testid={`checkout-${p.id}-btn`} disabled={current || busy} onClick={() => checkout(key)}
                className="mt-8 w-full rounded-full bg-[#00FF9D] py-3 font-semibold text-[#050505] hover:shadow-[0_0_24px_rgba(0,255,157,0.5)] transition-shadow disabled:opacity-40">
                {current ? "Current plan" : busy === key ? "Redirecting…" : me?.access === "locked" ? `Subscribe to ${p.name}`
                  : samePlan ? `Switch to ${shown} billing` : me?.plan && me.plan !== "trial" ? `Switch to ${p.name}` : `Subscribe to ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>
      <p data-testid="billing-trial-note" className="mt-6 text-xs text-[#A3A3A3]">
        {me?.plan === "trial" && me?.access !== "locked" ? "Your free trial doesn't need a card. Subscribe any time; your remaining trial days carry over and you're charged when they run out." : "Secure checkout by Stripe."}
        {" "}Have a discount code? Enter it at checkout on monthly plans. Switching billing period or plan is prorated.
      </p>
      <CancelPlan me={me} />
      {me && me.access !== "locked" && <UsageChart />}
    </div>
  );
}
